import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { BarChart3, PieChart, RefreshCw, TrendingUp, ChevronLeft, ChevronRight, Eye, EyeOff, CalendarIcon, Lock, X } from 'lucide-react';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Time slots for the chart (4-hour intervals)
const TIME_SLOTS = [
  { label: '01:00am - 05:00am', start: 1, end: 5 },
  { label: '05:00am - 09:00am', start: 5, end: 9 },
  { label: '09:00am - 01:00pm', start: 9, end: 13 },
  { label: '01:00pm - 05:00pm', start: 13, end: 17 },
  { label: '05:00pm - 09:00pm', start: 17, end: 21 },
  { label: '09:00pm - 01:00am', start: 21, end: 25 }, // 25 means 1am next day
];

const SalesInsights = ({ franchiseId, franchisePin }) => {
  const [chartType, setChartType] = useState('bar');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isLoading, setIsLoading] = useState(false);
  const [dayOrders, setDayOrders] = useState([]);
  
  // Security state - HIDDEN by default
  const [isAmountVisible, setIsAmountVisible] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [enteredPin, setEnteredPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [autoHideTimer, setAutoHideTimer] = useState(null);
  
  // Date picker state
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  // Auto-hide amounts after 10 seconds
  useEffect(() => {
    if (isAmountVisible) {
      // Clear any existing timer
      if (autoHideTimer) {
        clearTimeout(autoHideTimer);
      }
      // Set new timer to hide after 10 seconds
      const timer = setTimeout(() => {
        setIsAmountVisible(false);
      }, 10000);
      setAutoHideTimer(timer);
    }
    
    // Cleanup on unmount
    return () => {
      if (autoHideTimer) {
        clearTimeout(autoHideTimer);
      }
    };
  }, [isAmountVisible]);

  // Format date for display
  const formatDate = (date) => {
    return date.toLocaleDateString('en-IN', { 
      day: 'numeric',
      month: 'short'
    });
  };

  // Format date for API
  const formatDateForAPI = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Handle eye button click
  const handleToggleVisibility = () => {
    if (isAmountVisible) {
      // Hide values immediately
      setIsAmountVisible(false);
    } else {
      // Show PIN modal to unlock
      setShowPinModal(true);
      setEnteredPin('');
      setPinError('');
    }
  };

  // Verify PIN
  const handleVerifyPin = (e) => {
    e.preventDefault();
    // Convert both to strings and trim for comparison
    const inputPin = String(enteredPin).trim();
    const correctPin = String(franchisePin).trim();
    
    if (inputPin === correctPin) {
      setIsAmountVisible(true);
      setShowPinModal(false);
      setEnteredPin('');
      setPinError('');
    } else {
      setPinError('Incorrect PIN. Please try again.');
      setEnteredPin('');
    }
  };

  // Close PIN modal
  const handleClosePinModal = () => {
    setShowPinModal(false);
    setEnteredPin('');
    setPinError('');
  };

  // Handle date selection from calendar
  const handleDateSelect = (date) => {
    if (date && date <= new Date()) {
      setSelectedDate(date);
      setIsDatePickerOpen(false);
    }
  };

  // Format amount for display (hidden or visible)
  const formatAmount = (amount) => {
    if (!isAmountVisible) {
      return '₹••••';
    }
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  // Fetch orders for the selected date
  const fetchDayOrders = async () => {
    if (!franchiseId) return;
    
    setIsLoading(true);
    try {
      const dateStr = formatDateForAPI(selectedDate);
      const response = await axios.get(`${API}/orders`, {
        params: {
          franchise_id: franchiseId,
          start_date: dateStr,
          end_date: dateStr,
          limit: 1000
        }
      });
      
      const orders = response.data.orders || [];
      setDayOrders(orders);
    } catch (error) {
      console.error('Error fetching orders for insights:', error);
      setDayOrders([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch orders when date or franchise changes
  useEffect(() => {
    fetchDayOrders();
  }, [selectedDate, franchiseId]);

  // Navigate to previous day
  const goToPreviousDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    setSelectedDate(newDate);
  };

  // Navigate to next day
  const goToNextDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    if (newDate <= new Date()) {
      setSelectedDate(newDate);
    }
  };

  // Check if selected date is today
  const isToday = () => {
    const today = new Date();
    return selectedDate.toDateString() === today.toDateString();
  };

  // Process orders into time slot data
  const processOrdersData = useMemo(() => {
    const validOrders = dayOrders.filter(order => !order.is_deleted);

    const slotData = TIME_SLOTS.map(slot => {
      const slotOrders = validOrders.filter(order => {
        if (!order.date_timestamp) return false;
        const orderDate = new Date(order.date_timestamp);
        const orderHour = orderDate.getHours();
        
        if (slot.end > 24) {
          return orderHour >= slot.start || orderHour < (slot.end - 24);
        }
        return orderHour >= slot.start && orderHour < slot.end;
      });

      const totalSales = slotOrders.reduce((sum, order) => sum + (parseFloat(order.total_value) || 0), 0);
      const orderCount = slotOrders.length;

      // Calculate by payment method
      const getPaymentMethodSales = (method) => {
        return slotOrders
          .filter(o => (o.payment_method || 'cash') === method)
          .reduce((s, o) => s + (parseFloat(o.total_value) || 0), 0);
      };

      return {
        label: slot.label,
        shortLabel: `${slot.start % 12 || 12}${slot.start < 12 ? 'am' : 'pm'} - ${slot.end % 12 || 12}${slot.end < 12 || slot.end >= 24 ? 'am' : 'pm'}`,
        sales: totalSales,
        orders: orderCount,
        // Payment method breakdown
        cash: getPaymentMethodSales('cash'),
        digital: getPaymentMethodSales('digital'),
        card: getPaymentMethodSales('card'),
        other: getPaymentMethodSales('other'),
        // Ledger breakdown (for reference)
        ledger: slotOrders.filter(o => o.ledger !== 'cash').reduce((s, o) => s + (parseFloat(o.total_value) || 0), 0),
      };
    });

    return slotData;
  }, [dayOrders]);

  // Calculate payment method totals for summary
  const paymentMethodTotals = useMemo(() => {
    const validOrders = dayOrders.filter(order => !order.is_deleted);
    return {
      cash: validOrders.filter(o => (o.payment_method || 'cash') === 'cash').reduce((s, o) => s + (parseFloat(o.total_value) || 0), 0),
      digital: validOrders.filter(o => o.payment_method === 'digital').reduce((s, o) => s + (parseFloat(o.total_value) || 0), 0),
      card: validOrders.filter(o => o.payment_method === 'card').reduce((s, o) => s + (parseFloat(o.total_value) || 0), 0),
      other: validOrders.filter(o => o.payment_method === 'other').reduce((s, o) => s + (parseFloat(o.total_value) || 0), 0),
      ledger: validOrders.filter(o => o.ledger && o.ledger !== 'cash').reduce((s, o) => s + (parseFloat(o.total_value) || 0), 0),
    };
  }, [dayOrders]);

  // Calculate totals
  const totalSales = processOrdersData.reduce((sum, slot) => sum + slot.sales, 0);
  const totalOrders = processOrdersData.reduce((sum, slot) => sum + slot.orders, 0);
  const maxSales = Math.max(...processOrdersData.map(s => s.sales), 1);

  // Peak hour
  const peakSlot = processOrdersData.reduce((max, slot) => slot.sales > max.sales ? slot : max, { sales: 0, label: '-' });

  // Refresh data
  const handleRefresh = () => {
    fetchDayOrders();
  };

  return (
    <>
      <Card className="shadow-sm border-border" data-testid="sales-insights-card">
        <CardHeader className="pb-2 sm:pb-3 px-3 sm:px-6">
          <div className="flex flex-col gap-3">
            {/* Title Row */}
            <div className="flex items-center justify-between">
              <CardTitle className="text-base sm:text-lg font-bold text-primary flex items-center gap-2">
                <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-accent" />
                Sales Insights
                {!isAmountVisible && <Lock className="h-3 w-3 sm:h-4 sm:w-4 text-orange-500" />}
              </CardTitle>
              
              {/* Action buttons - Desktop */}
              <div className="hidden sm:flex items-center gap-2">
                {/* Eye Button */}
                <button
                  onClick={handleToggleVisibility}
                  data-testid="toggle-visibility-btn"
                  className={`p-2 rounded-full transition-all ${
                    isAmountVisible 
                      ? 'bg-green-100 text-green-600 hover:bg-green-200' 
                      : 'bg-orange-100 text-orange-600 hover:bg-orange-200'
                  }`}
                  title={isAmountVisible ? 'Hide amounts' : 'Show amounts (PIN required)'}
                >
                  {isAmountVisible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                </button>

                {/* Refresh Button */}
                <button
                  onClick={handleRefresh}
                  data-testid="refresh-btn"
                  className={`p-2 rounded-full hover:bg-muted transition-all ${isLoading ? 'animate-spin' : ''}`}
                  title="Refresh data"
                >
                  <RefreshCw className="h-4 w-4 text-secondary" />
                </button>
              </div>
            </div>
            
            {/* Controls Row */}
            <div className="flex items-center justify-between gap-2 flex-wrap">
              {/* Left side - Chart Toggle + Date Picker */}
              <div className="flex items-center gap-2">
                {/* Chart Type Toggle */}
                <div className="flex items-center bg-muted rounded-full p-0.5 sm:p-1">
                  <button
                    onClick={() => setChartType('bar')}
                    data-testid="bar-chart-btn"
                    className={`flex items-center gap-1 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all ${
                      chartType === 'bar' 
                        ? 'bg-white text-primary shadow-sm' 
                        : 'text-secondary hover:text-primary'
                    }`}
                  >
                    <BarChart3 className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span className="hidden xs:inline">Bar</span>
                  </button>
                  <button
                    onClick={() => setChartType('pie')}
                    data-testid="pie-chart-btn"
                    className={`flex items-center gap-1 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all ${
                      chartType === 'pie' 
                        ? 'bg-blue-500 text-white shadow-sm' 
                        : 'text-secondary hover:text-primary'
                    }`}
                  >
                    <PieChart className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span className="hidden xs:inline">Pie</span>
                  </button>
                </div>

                {/* Date Picker */}
                <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
                  <PopoverTrigger asChild>
                    <button 
                      data-testid="date-picker-btn"
                      className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 bg-muted rounded-full hover:bg-muted/80 transition-all"
                    >
                      <CalendarIcon className="h-3 w-3 sm:h-4 sm:w-4 text-secondary" />
                      <span className="text-xs sm:text-sm font-semibold text-primary">
                        {isToday() ? 'Today' : format(selectedDate, 'dd MMM')}
                      </span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent 
                    className="w-auto p-0 bg-white border border-gray-200 shadow-xl rounded-lg" 
                    align="start"
                    sideOffset={8}
                  >
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={handleDateSelect}
                      disabled={(date) => date > new Date()}
                      initialFocus
                      className="bg-white rounded-lg"
                      classNames={{
                        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                        month: "space-y-4",
                        caption: "flex justify-center pt-1 relative items-center",
                        caption_label: "text-sm font-semibold text-gray-900",
                        nav: "space-x-1 flex items-center",
                        nav_button: "h-7 w-7 bg-gray-100 hover:bg-gray-200 rounded-md p-0 flex items-center justify-center transition-colors",
                        nav_button_previous: "absolute left-1",
                        nav_button_next: "absolute right-1",
                        table: "w-full border-collapse space-y-1",
                        head_row: "flex",
                        head_cell: "text-gray-500 rounded-md w-9 font-medium text-[0.8rem]",
                        row: "flex w-full mt-2",
                        cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-transparent",
                        day: "h-9 w-9 p-0 font-normal rounded-md hover:bg-gray-100 transition-colors flex items-center justify-center",
                        day_selected: "bg-[#3E2723] text-white hover:bg-[#5D4037] hover:text-white focus:bg-[#3E2723] focus:text-white font-semibold",
                        day_today: "bg-orange-100 text-orange-800 font-semibold",
                        day_outside: "text-gray-300 opacity-50",
                        day_disabled: "text-gray-300 opacity-50 cursor-not-allowed",
                        day_hidden: "invisible",
                      }}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Right side - Date Nav + Mobile Actions */}
              <div className="flex items-center gap-1 sm:gap-2">
                {/* Date Navigator Arrows */}
                <button
                  onClick={goToPreviousDay}
                  data-testid="prev-day-btn"
                  className="p-1.5 sm:p-2 rounded-full hover:bg-muted transition-all"
                  title="Previous day"
                >
                  <ChevronLeft className="h-4 w-4 text-secondary" />
                </button>
                <button
                  onClick={goToNextDay}
                  data-testid="next-day-btn"
                  disabled={isToday()}
                  className={`p-1.5 sm:p-2 rounded-full transition-all ${
                    isToday() 
                      ? 'opacity-30 cursor-not-allowed' 
                      : 'hover:bg-muted'
                  }`}
                  title="Next day"
                >
                  <ChevronRight className="h-4 w-4 text-secondary" />
                </button>

                {/* Mobile only - Eye & Refresh */}
                <div className="flex sm:hidden items-center gap-1">
                  <button
                    onClick={handleToggleVisibility}
                    data-testid="toggle-visibility-btn-mobile"
                    className={`p-1.5 rounded-full transition-all ${
                      isAmountVisible 
                        ? 'bg-green-100 text-green-600' 
                        : 'bg-orange-100 text-orange-600'
                    }`}
                  >
                    {isAmountVisible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </button>
                  <button
                    onClick={handleRefresh}
                    className={`p-1.5 rounded-full hover:bg-muted transition-all ${isLoading ? 'animate-spin' : ''}`}
                  >
                    <RefreshCw className="h-4 w-4 text-secondary" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="px-3 sm:px-6 pb-4">
          {/* Legend - Payment Methods + Ledger */}
          <div className="flex items-center justify-end gap-2 sm:gap-3 mb-3 sm:mb-4 text-[10px] sm:text-xs flex-wrap">
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-green-500" />
              <span className="text-secondary">Cash</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-blue-500" />
              <span className="text-secondary">Digital</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-purple-500" />
              <span className="text-secondary">Card</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-gray-400" />
              <span className="text-secondary">Other</span>
            </div>
            <div className="flex items-center gap-1 pl-1 border-l border-border">
              <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-amber-500" />
              <span className="text-secondary">Ledger</span>
            </div>
          </div>

          {chartType === 'bar' ? (
            /* Bar Chart */
            <div className="relative">
              {/* Chart Container */}
              <div className="flex items-end justify-between gap-1 sm:gap-2 h-36 sm:h-48 md:h-56 mb-2 px-1">
                {processOrdersData.map((slot, index) => {
                  const barHeight = maxSales > 0 ? (slot.sales / maxSales) * 100 : 0;
                  
                  return (
                    <div key={index} className="flex-1 flex flex-col items-center justify-end group h-full min-w-0">
                      {/* Value Label */}
                      {slot.sales > 0 && (
                        <div className="text-[10px] sm:text-xs font-semibold text-primary mb-1 opacity-0 group-hover:opacity-100 sm:opacity-100 transition-opacity whitespace-nowrap truncate max-w-full">
                          {formatAmount(slot.sales)}
                        </div>
                      )}
                      
                      {/* Bar Container */}
                      <div className="w-full max-w-[40px] sm:max-w-[50px] flex flex-col justify-end" style={{ height: 'calc(100% - 20px)' }}>
                        {slot.sales > 0 ? (
                          <div 
                            className="w-full rounded-t-md sm:rounded-t-lg overflow-hidden transition-all duration-500 ease-out flex flex-col"
                            style={{ height: `${Math.max(barHeight, 5)}%` }}
                          >
                            {/* Stacked segments with minimum visible height */}
                            {(() => {
                              // Calculate segments with minimum visibility
                              const segments = [
                                { key: 'ledger', value: slot.ledger, color: 'bg-amber-500', minHeight: 8 },
                                { key: 'other', value: slot.other, color: 'bg-gray-400', minHeight: 8 },
                                { key: 'card', value: slot.card, color: 'bg-purple-500', minHeight: 8 },
                                { key: 'digital', value: slot.digital, color: 'bg-blue-500', minHeight: 8 },
                                { key: 'cash', value: slot.cash, color: 'bg-gradient-to-b from-green-400 to-green-500', minHeight: 0 },
                              ].filter(s => s.value > 0);
                              
                              const totalValue = segments.reduce((sum, s) => sum + s.value, 0);
                              
                              // Calculate heights with minimum visibility for small segments
                              const segmentHeights = segments.map(s => {
                                const naturalPercent = (s.value / totalValue) * 100;
                                // If segment is less than minHeight%, give it minHeight% visibility
                                return {
                                  ...s,
                                  height: naturalPercent < s.minHeight && s.value > 0 ? Math.max(naturalPercent, s.minHeight) : naturalPercent
                                };
                              });
                              
                              // Normalize heights to ensure they sum to 100%
                              const totalHeight = segmentHeights.reduce((sum, s) => sum + s.height, 0);
                              const normalizedSegments = segmentHeights.map(s => ({
                                ...s,
                                height: (s.height / totalHeight) * 100
                              }));
                              
                              return normalizedSegments.map((segment) => (
                                <div 
                                  key={segment.key}
                                  className={`w-full ${segment.color} transition-all ${segment.key === 'cash' ? 'flex-1' : ''}`}
                                  style={{ 
                                    height: segment.key === 'cash' ? undefined : `${segment.height}%`,
                                    minHeight: segment.key !== 'cash' && segment.value > 0 ? '4px' : undefined
                                  }}
                                  title={`${segment.key}: ₹${segment.value.toLocaleString('en-IN')}`}
                                />
                              ));
                            })()}
                          </div>
                        ) : (
                          <div className="w-full h-1 bg-gray-200 rounded-full" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* X-Axis Line */}
              <div className="h-0.5 bg-gray-200 mx-1" />
              
              {/* X-Axis Labels */}
              <div className="flex justify-between gap-1 sm:gap-2 mt-1 sm:mt-2 px-1">
                {processOrdersData.map((slot, index) => (
                  <div key={index} className="flex-1 text-center min-w-0">
                    <p className="text-[8px] sm:text-[10px] md:text-xs text-secondary leading-tight whitespace-pre-line truncate">
                      {slot.shortLabel.replace(' - ', '\n')}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            /* Pie Chart - Payment Method Breakdown */
            <div className="flex flex-col items-center gap-4 sm:gap-6 py-2 sm:py-4">
              {/* Pie Chart SVG */}
              <div className="relative w-36 h-36 sm:w-48 sm:h-48">
                <svg viewBox="0 0 100 100" className="transform -rotate-90">
                  {totalSales > 0 ? (
                    <>
                      {/* Payment method segments */}
                      {[
                        { name: 'Cash', value: paymentMethodTotals.cash, color: '#22C55E' },
                        { name: 'Digital', value: paymentMethodTotals.digital, color: '#3B82F6' },
                        { name: 'Card', value: paymentMethodTotals.card, color: '#8B5CF6' },
                        { name: 'Other', value: paymentMethodTotals.other, color: '#9CA3AF' },
                        { name: 'Ledger', value: paymentMethodTotals.ledger, color: '#F59E0B' },
                      ].map((method, index, arr) => {
                        const percentage = (method.value / totalSales) * 100;
                        const offset = arr
                          .slice(0, index)
                          .reduce((sum, m) => sum + (m.value / totalSales) * 100, 0);
                        
                        return percentage > 0 ? (
                          <circle
                            key={method.name}
                            cx="50"
                            cy="50"
                            r="40"
                            fill="transparent"
                            stroke={method.color}
                            strokeWidth="20"
                            strokeDasharray={`${percentage * 2.51} 251`}
                            strokeDashoffset={`${-offset * 2.51}`}
                            className="transition-all duration-500"
                          />
                        ) : null;
                      })}
                    </>
                  ) : (
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      fill="transparent"
                      stroke="#E5E7EB"
                      strokeWidth="20"
                    />
                  )}
                </svg>
                
                {/* Center Text */}
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <p className="text-lg sm:text-2xl font-bold text-primary">
                    {formatAmount(totalSales)}
                  </p>
                  <p className="text-[10px] sm:text-xs text-secondary">{totalOrders} orders</p>
                </div>
              </div>
              
              {/* Payment Method Legend */}
              <div className="grid grid-cols-2 gap-2 sm:gap-3 w-full max-w-xs">
                {[
                  { name: 'Cash', value: paymentMethodTotals.cash, color: 'bg-green-500' },
                  { name: 'Digital', value: paymentMethodTotals.digital, color: 'bg-blue-500' },
                  { name: 'Card', value: paymentMethodTotals.card, color: 'bg-purple-500' },
                  { name: 'Other', value: paymentMethodTotals.other, color: 'bg-gray-400' },
                  { name: 'Ledger', value: paymentMethodTotals.ledger, color: 'bg-amber-500' },
                ].map((method) => (
                  method.value > 0 ? (
                    <div key={method.name} className="flex items-center gap-1.5 text-xs sm:text-sm">
                      <div className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full flex-shrink-0 ${method.color}`} />
                      <span className="text-secondary">{method.name}</span>
                      <span className={`font-semibold ${method.name === 'Ledger' ? 'text-amber-600' : 'text-primary'}`}>{formatAmount(method.value)}</span>
                    </div>
                  ) : null
                ))}
              </div>
            </div>
          )}

          {/* Summary Stats */}
          <div className="grid grid-cols-3 gap-2 sm:gap-3 mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-border">
            <div className="text-center">
              <p className="text-base sm:text-xl md:text-2xl font-bold text-primary" data-testid="total-sales">
                {formatAmount(totalSales)}
              </p>
              <p className="text-[10px] sm:text-xs text-secondary">Total Sales</p>
            </div>
            <div className="text-center">
              <p className="text-base sm:text-xl md:text-2xl font-bold text-accent" data-testid="total-orders">
                {totalOrders}
              </p>
              <p className="text-[10px] sm:text-xs text-secondary">Orders</p>
            </div>
            <div className="text-center">
              <p className="text-xs sm:text-sm font-bold text-green-600 truncate" data-testid="peak-hour">
                {peakSlot.sales > 0 ? peakSlot.shortLabel.split(' - ')[0] : '-'}
              </p>
              <p className="text-[10px] sm:text-xs text-secondary">Peak Hour</p>
            </div>
          </div>

          {/* Payment Method Breakdown - Compact row */}
          {totalSales > 0 && (
            <div className="flex items-center justify-center gap-2 sm:gap-4 mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-border/50 flex-wrap">
              {paymentMethodTotals.cash > 0 && (
                <div className="flex items-center gap-1 text-[10px] sm:text-xs">
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                  <span className="text-secondary">Cash:</span>
                  <span className="font-semibold text-primary">{formatAmount(paymentMethodTotals.cash)}</span>
                </div>
              )}
              {paymentMethodTotals.digital > 0 && (
                <div className="flex items-center gap-1 text-[10px] sm:text-xs">
                  <div className="w-2 h-2 rounded-full bg-blue-500" />
                  <span className="text-secondary">Digital:</span>
                  <span className="font-semibold text-primary">{formatAmount(paymentMethodTotals.digital)}</span>
                </div>
              )}
              {paymentMethodTotals.card > 0 && (
                <div className="flex items-center gap-1 text-[10px] sm:text-xs">
                  <div className="w-2 h-2 rounded-full bg-purple-500" />
                  <span className="text-secondary">Card:</span>
                  <span className="font-semibold text-primary">{formatAmount(paymentMethodTotals.card)}</span>
                </div>
              )}
              {paymentMethodTotals.other > 0 && (
                <div className="flex items-center gap-1 text-[10px] sm:text-xs">
                  <div className="w-2 h-2 rounded-full bg-gray-400" />
                  <span className="text-secondary">Other:</span>
                  <span className="font-semibold text-primary">{formatAmount(paymentMethodTotals.other)}</span>
                </div>
              )}
              {paymentMethodTotals.ledger > 0 && (
                <div className="flex items-center gap-1 text-[10px] sm:text-xs pl-1 border-l border-border">
                  <div className="w-2 h-2 rounded-full bg-amber-500" />
                  <span className="text-secondary">Ledger:</span>
                  <span className="font-semibold text-amber-600">{formatAmount(paymentMethodTotals.ledger)}</span>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* PIN Entry Modal */}
      {showPinModal && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={handleClosePinModal}
        >
          <div 
            className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-5 sm:p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="pin-modal"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Lock className="h-5 w-5 text-orange-500" />
                <h3 className="text-lg font-bold text-gray-900">Enter Franchise PIN</h3>
              </div>
              <button 
                onClick={handleClosePinModal}
                className="p-1 rounded-full hover:bg-gray-100 transition-colors"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            
            <p className="text-sm text-gray-600 mb-4">
              Enter your franchise PIN to view sales amounts.
            </p>
            
            <form onSubmit={handleVerifyPin}>
              <Input
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="Enter PIN"
                value={enteredPin}
                onChange={(e) => {
                  setEnteredPin(e.target.value);
                  setPinError('');
                }}
                className="w-full text-center text-lg tracking-widest mb-3"
                autoFocus
                data-testid="pin-input"
              />
              
              {pinError && (
                <p className="text-red-500 text-sm text-center mb-3" data-testid="pin-error">
                  {pinError}
                </p>
              )}
              
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClosePinModal}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-[#3E2723] hover:bg-[#5D4037] text-white"
                  data-testid="verify-pin-btn"
                >
                  Unlock
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default SalesInsights;
